import { useGame } from 'contexts/Game/Game';
import * as d3 from 'd3';
import { getOrdinal } from 'helpers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as setup from 'setup';
import { useInterval } from 'usehooks-ts';
import Chart from '../Chart/Chart';

const axes = {
    labelX: 'Hands',
    labelY: '$',
    prefixY: '$',
    suffixY: '',
};

const lineColors = {
    first: '#6C60FF',
    last: '#CE2A96',
};

export default function TrialsChart({ index = -1, strategy }: TrialsChartProps) {
    const [animatingTrial, setAnimatingTrial] = useState(-1);
    const [animatingTrialHand, setAnimatingTrialHand] = useState<number | null>(null);
    const [chartRect, setChartRect] = useState<DOMRect>(new DOMRect());
    const [container, setContainer] = useState(d3.select(null));
    const game = useGame();

    const containerRef = useCallback((ref: any) => {
        const newContainer = d3.select(ref);
        setContainer(newContainer);
    }, []);

    const trials = useMemo(() => {
        return setup.charts[strategy].trials;
    }, [strategy]);

    const lines = useMemo(() => {
        // Get lines from trials
        return trials.map((trial, index) => ({
            color: animatingTrial > 0 && index === animatingTrial ? lineColors.last : lineColors.first,
            label: `${getOrdinal(index + 1)} trial`,
            values: trial.map(hand => hand[2]),
            visible: index <= animatingTrial,
        }));
    }, [animatingTrial, trials]);

    const scaleX = useMemo(() => {
        const maxX = Math.max(...lines.map(line => line.values.length));
        return d3
            .scaleLinear()
            .domain([0, maxX * 1.2])
            .range([0, chartRect.width]);
    }, [chartRect, lines]);

    const scaleY = useMemo(() => {
        const linesValues = lines.reduce((values, line) => [...values, ...line.values], [0]);
        const maxY = Math.max(0, ...linesValues);
        return d3
            .scaleLinear()
            .domain([0, maxY * 1.2])
            .range([chartRect.height, 0]);
    }, [chartRect, lines]);

    useEffect(() => {
        // Animate trial
        if (animatingTrial !== index && index >= 0) {
            setAnimatingTrial(index);
            setAnimatingTrialHand(0);
        }
    }, [animatingTrial, index]);

    useInterval(
        async () => {
            const trial = trials[index];

            // Set next trial hand
            let newAnimatingTrialHand = null;
            if (animatingTrialHand !== null && trial && animatingTrialHand < trial.length - 1) {
                newAnimatingTrialHand = animatingTrialHand + 1;
            }
            setAnimatingTrialHand(newAnimatingTrialHand);

            if (!newAnimatingTrialHand) {
                return;
            }

            // Reset
            game.setResultType(null);
            await game.setCardsSides('back', 'back');

            // Get next hand
            const hand = trial[newAnimatingTrialHand];
            const cards = hand[0].split(',') as GameCardType[];
            const holeCards = cards.slice(0, 2);
            const communityCards = cards.slice(2);

            // Show hand cards
            game.setHoleCards(holeCards);
            game.setCommunityCards(communityCards);
            await game.setCardsSides('front', 'front');

            // Show results
            game.setResultType(hand[1] as GameResultType);
        },
        (animatingTrialHand && 600) || animatingTrialHand
    );

    useEffect(() => {
        if (!chartRect.width) {
            return;
        }

        // Get data
        const visibleLines = lines.filter(line => line.visible);
        const data = visibleLines.reduce((segments, line, index) => {
            const animating = index === visibleLines.length - 1 && animatingTrialHand !== null;

            // Get values
            const offset = animating ? animatingTrialHand + 1 : line.values.length;
            const values = line.values.slice(0, offset);

            // Get segment
            const segment = values.slice(1).map((value, index) => ({
                color: line.color,
                delay: animating ? 400 : 0,
                duration: animating ? 200 : 0,
                x1: scaleX(index),
                y1: scaleY(values[index] || value),
                x2: scaleX(index + 1),
                y2: scaleY(value),
                label: `<div>${line.label}</div>
                        <div>HAND: <b>${index + 1}</b></div>
                        <div>$: <b>${value.toLocaleString()}</b></div>`,
            }));

            // Add segment
            return segments.concat(segment);
        }, [] as TrialsChartLineSegment[]);

        // Add lines
        const elements = container.selectAll('line').data(data);

        elements.exit().remove();

        const enterElements = elements
            .enter()
            .append('line')
            .attr('x1', d => d.x1)
            .attr('x2', d => d.x1)
            .attr('y1', d => d.y1)
            .attr('y2', d => d.y1)
            .attr('data-tooltip-html', d => d.label)
            .attr('fill', 'none')
            .attr('stroke', d => d.color)
            .attr('stroke-linecap', 'round')
            .attr('stroke-width', 5)
            .attr('data-tooltip-id', 'chart-tooltip');

        enterElements
            .merge(elements as any)
            .transition()
            .duration(d => d.duration)
            .delay(d => d.delay)
            .attr('stroke', d => d.color)
            .attr('x1', d => d.x1)
            .attr('x2', d => d.x2)
            .attr('y1', d => d.y1)
            .attr('y2', d => d.y2);
    }, [animatingTrialHand, chartRect, container, lines, scaleX, scaleY]);

    return (
        <Chart
            axes={axes}
            scaleX={scaleX}
            scaleY={scaleY}
            title={`Results from ${setup.charts[strategy].label} strategy`}
            onRectChange={setChartRect}
        >
            <g ref={containerRef} />
        </Chart>
    );
}
